import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { ClipboardCheck, CheckCircle2, ChevronRight, ChevronDown, ChevronUp, CalendarDays, XCircle, Thermometer, ShieldCheck, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Select, Spinner, StatusBadge, Field, inputCls } from "../../components/ui-kit";
import ControlsCalendar from "../../components/ControlsCalendar";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";
import { periodLabel, conformingValue, SingleRegistration } from "./ConformityRegistry";

const todayKey = () => { const date = new Date(); return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; };

const isTemperatureControl = (procedure) => procedure.module === "temperature" && ["analytic", "general"].includes(procedure.temperature_mode);
const deviceFields = () => ({ device_time: new Date().toISOString(), device_info: navigator.userAgent.slice(0, 80), online: navigator.onLine });

// Attrezzature a temperatura coperte da una procedura: stessa regola della pagina
// Temperature (procedura del reparto, altrimenti quella generale della sede).
const temperatureItemsFor = (procedure, equipment, procedures) => equipment.filter((item) => {
  if (item.site_id !== procedure.site_id) return false;
  if (procedure.department_id) return item.department_id === procedure.department_id;
  return !procedures.some((other) => isTemperatureControl(other) && other.temperature_mode === procedure.temperature_mode && other.site_id === item.site_id && other.department_id && other.department_id === item.department_id);
});

const validNumber = (value) => value !== "" && value !== undefined && Number.isFinite(Number(String(value).replace(",", ".")));
const rangeLabel = (range = {}) => `${range.min ?? "—"} / ${range.max ?? "—"} °C`;
const outOfRange = (range = {}, value) => {
  if (!validNumber(value)) return false;
  const number = Number(String(value).replace(",", "."));
  return (range.min != null && number < range.min) || (range.max != null && number > range.max);
};

// Temperature direttamente nella riga: conferma rapida (modalità generale) o
// valore per ogni attrezzatura (modalità analitica).
function TemperatureInline({ procedure, items, selectedDate, onDone }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const general = procedure.temperature_mode === "general";
  if (!items.length) return <p className="text-sm text-amber-700">Nessuna attrezzatura a temperatura configurata per questo reparto. <Link to="/app/temperature" className="font-semibold underline">Apri il registro temperature</Link></p>;
  const save = async () => {
    if (!general) {
      const missing = items.filter((item) => !validNumber(values[item.id]));
      if (missing.length) { toast.error(`Inserisci la temperatura di tutte le attrezzature: ne mancano ${missing.length}`); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/app/registrations", {
        procedure_id: procedure.id,
        site_id: procedure.site_id,
        department_id: procedure.department_id || null,
        equipment_values: general
          ? items.map((item) => ({ equipment_id: item.id, conforming: true }))
          : items.map((item) => ({ equipment_id: item.id, value: Number(String(values[item.id]).replace(",", ".")) })),
        general_declaration: general,
        reference_date: selectedDate,
        note: "",
        ...deviceFields(),
      });
      if (data.registration?.status === "non_conforme") toast.warning("Temperatura fuori limite: è stata aperta una non conformità.");
      else toast.success("Temperature registrate");
      onDone();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (general) return <div className="space-y-2">
    <p className="text-xs text-slate-500">{items.map((item) => item.name).join(", ")}</p>
    <Button variant="success" className="w-full sm:w-auto" loading={saving} onClick={save} data-testid={`inline-temp-general-${procedure.id}`}><CheckCircle2 className="h-5 w-5" />Tutte conformi ({items.length})</Button>
    <p className="text-xs text-slate-500">Se un’attrezzatura è fuori limite <Link to="/app/temperature" className="font-semibold text-navy underline">registrala nel registro temperature</Link>.</p>
  </div>;
  return <div className="space-y-2">
    {items.map((item) => { const bad = outOfRange(item.temp_range, values[item.id]); return <label key={item.id} className="flex items-center gap-3">
      <span className="min-w-0 flex-1 text-sm"><span className="block font-medium text-navy">{item.name}</span><span className="text-xs text-slate-400">Limiti {rangeLabel(item.temp_range)}</span></span>
      <input type="text" inputMode="decimal" value={values[item.id] ?? ""} onChange={(event) => setValues((current) => ({ ...current, [item.id]: event.target.value }))} placeholder="°C" className={`${inputCls} !w-24 text-center ${bad ? "!border-red-500 !ring-1 !ring-red-300" : ""}`} aria-label={`Temperatura ${item.name}`} data-testid={`inline-temp-${procedure.id}-${item.id}`} />
    </label>; })}
    <Button variant="success" className="w-full" loading={saving} onClick={save} data-testid={`inline-temp-save-${procedure.id}`}><Thermometer className="h-5 w-5" />Salva temperature</Button>
  </div>;
}

// Dati per registrare un controllo come conforme senza altri input; null se il
// controllo richiede un valore (temperature analitiche, numeri, testo), è un
// CCP (niente dichiarazione generale) o si registra al ricevimento merci.
const bulkPayloadFor = (procedure, temperatureItems) => {
  if (procedure.receipt_linked) return null;
  if (isTemperatureControl(procedure)) {
    if (procedure.temperature_mode !== "general" || procedure.is_ccp || !temperatureItems.length) return null;
    return { value: null, general_declaration: true, equipment_values: temperatureItems.map((item) => ({ equipment_id: item.id, conforming: true })) };
  }
  if (procedure.temperature_mode === "general") return procedure.is_ccp ? null : { value: null, general_declaration: true };
  const value = conformingValue(procedure);
  return value === undefined || value === null ? null : { value, general_declaration: false };
};

// Da fare nella giornata: giornalieri non registrati + periodici già dovuti.
const isPendingProcedure = (procedure) => !procedure.completed && (!procedure.period || procedure.period === "day" || procedure.due_now);
// Controlli della giornata attestabili come conformi con un solo clic.
const bulkItemsFor = (procedures, equipment) => procedures
  .filter(isPendingProcedure)
  .map((procedure) => ({ procedure, payload: bulkPayloadFor(procedure, isTemperatureControl(procedure) ? temperatureItemsFor(procedure, equipment, procedures) : []) }))
  .filter((item) => item.payload);
const postBulk = (items, date) => Promise.allSettled(items.map(({ procedure, payload }) => api.post("/app/registrations", {
  procedure_id: procedure.id,
  site_id: procedure.site_id,
  department_id: procedure.department_id || null,
  equipment_values: [],
  ...payload,
  reference_date: date,
  note: "",
  ...deviceFields(),
})));
const shortDay = (date) => new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "numeric", month: "short" }).format(new Date(`${date}T12:00:00`));

// Esito del controllo direttamente nella riga del reparto, senza cambiare pagina.
function InlineControl({ procedure, selectedDate, onDone, temperatureItems, readOnly }) {
  const [saving, setSaving] = useState(false);
  const [nonConforming, setNonConforming] = useState(false);
  if (readOnly) return <p className="text-sm text-slate-400">Sola consultazione</p>;
  if (procedure.receipt_linked) return <Link to="/app/carica-lotti" className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-navy/30 px-3 text-sm font-semibold text-navy hover:bg-slate-50">Si registra al ricevimento merci <ChevronRight className="h-4 w-4" /></Link>;
  if (isTemperatureControl(procedure)) return <TemperatureInline procedure={procedure} items={temperatureItems} selectedDate={selectedDate} onDone={onDone} />;
  const generalDeclaration = procedure.temperature_mode === "general";
  if (conformingValue(procedure) !== "conforme" && !generalDeclaration) return <SingleRegistration procedure={procedure} selectedDate={selectedDate} onDone={onDone} />;
  if (nonConforming) return <SingleRegistration procedure={procedure} selectedDate={selectedDate} initialValue="non_conforme" onDone={onDone} onCancel={() => setNonConforming(false)} />;
  const saveConforming = async () => {
    setSaving(true);
    try {
      await api.post("/app/registrations", {
        procedure_id: procedure.id,
        site_id: procedure.site_id,
        department_id: procedure.department_id || null,
        value: conformingValue(procedure),
        general_declaration: generalDeclaration,
        reference_date: selectedDate,
        note: "",
        ...deviceFields(),
      });
      toast.success("Controllo registrato come conforme");
      onDone();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return <div className="flex gap-2">
    <Button variant="success" className="min-h-[48px] flex-1" loading={saving} onClick={saveConforming} data-testid={`inline-conforme-${procedure.id}`}><CheckCircle2 className="h-5 w-5" />Conforme</Button>
    <Button variant="outline" className="min-h-[48px] flex-1 !border-red-300 !text-red-700 hover:!bg-red-50" disabled={saving} onClick={() => setNonConforming(true)} data-testid={`inline-non-conforme-${procedure.id}`}><XCircle className="h-5 w-5" />Non conforme</Button>
  </div>;
}

// Pagina "Controlli di oggi": elenco procedure programmate per la sede selezionata.
export default function ControlliOggi() {
  const { ctx } = useOutletContext();
  const sites = ctx.sites || [];
  const readOnly = ctx.user?.role === "ispettore";
  const [siteId, setSiteId] = useState(sites[0]?.id || "");
  const [data, setData] = useState(null);
  // Nessun giorno preselezionato: si sceglie dal calendario, dove lampeggia il
  // giorno da recuperare per primo. Se non manca nulla si apre su oggi.
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const [equipment, setEquipment] = useState([]);

  const load = useCallback((quiet = false) => {
    if (!siteId || !selectedDate) return Promise.resolve();
    if (!quiet) setData(null);
    return api.get(`/app/procedures/today?site_id=${siteId}&date_key=${selectedDate}`).then(({ data }) => setData(data)).catch(() => setData({ procedures: [] }));
  }, [selectedDate, siteId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/app/equipment").then(({ data }) => setEquipment((data.equipment || []).filter((item) => item.active !== false && item.temperature_controlled !== false && (item.temp_range?.min != null || item.temp_range?.max != null)))).catch(() => setEquipment([]));
  }, []);
  // Esito del pulsante "Attesta la conformità" e apertura della registrazione
  // dettagliata: valgono solo per il giorno scelto.
  const [bulkDone, setBulkDone] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const resetDayView = () => { setBulkDone(0); setShowDetail(false); };
  // Selezione multipla: più giorni rossi attestati con un solo clic.
  const [multiMode, setMultiMode] = useState(false);
  const [multiDates, setMultiDates] = useState([]);
  const [multiSaving, setMultiSaving] = useState(false);
  const [hasMissingDays, setHasMissingDays] = useState(false);
  const exitMultiMode = () => { setMultiMode(false); setMultiDates([]); };
  const changeSite = (value) => { resetDayView(); exitMultiMode(); setSiteId(value); setSelectedDate(null); setData(null); };
  const onDaysLoaded = useCallback((days, pastMissing = []) => {
    const missing = days.some((day) => day.status === "missing") || pastMissing.length > 0;
    setHasMissingDays(missing);
    setSelectedDate((current) => current || (missing ? null : todayKey()));
  }, []);
  const onRegistered = useCallback(() => { setCalendarRefresh((current) => current + 1); load(true); }, [load]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const listRef = useRef(null);
  const bulkRef = useRef(null);
  // Scelto il giorno dal calendario, porta subito al pulsante "Attesta la conformità"
  // (o all'elenco se non ce n'è), lasciandolo sotto la barra fissa in alto.
  const scrollPending = useRef(false);
  const chooseDay = (date) => {
    if (multiMode) { setMultiDates((current) => current.includes(date) ? current.filter((item) => item !== date) : [...current, date].sort()); return; }
    resetDayView(); scrollPending.current = true; setSelectedDate(date);
  };
  // Tasto rapido del menu (?giorno=oggi): apre subito la giornata di oggi con il
  // pulsante "Attesta la conformità" in vista; i giorni da recuperare restano
  // segnalati nel calendario.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("giorno") !== "oggi") return;
    chooseDay(todayKey());
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data || !scrollPending.current) return;
    scrollPending.current = false;
    window.requestAnimationFrame(() => (bulkRef.current || listRef.current)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [data]);
  // Da fare oggi: giornalieri non registrati + periodici negli ultimi giorni del periodo.
  const pendingProcedures = (data?.procedures || []).filter(isPendingProcedure);
  const periodicProcedures = (data?.procedures || []).filter((procedure) => procedure.period && procedure.period !== "day" && !procedure.due_now && !procedure.completed);
  const bulkItems = readOnly ? [] : bulkItemsFor(data?.procedures || [], equipment);
  const manualCount = pendingProcedures.length - bulkItems.length;
  // Con il pulsante unico disponibile l'elenco riga per riga resta chiuso, così
  // il cliente vede un solo pulsante; senza pulsante unico è sempre aperto.
  const detailOpen = showDetail || bulkItems.length === 0;
  const saveAllConforming = async () => {
    setBulkSaving(true);
    const results = await postBulk(bulkItems, selectedDate);
    const failed = results.filter((result) => result.status === "rejected");
    if (failed.length) toast.error(`${bulkItems.length - failed.length} controlli registrati, ${failed.length} non salvati: ${formatApiErrorDetail(failed[0].reason?.response?.data?.detail)}`);
    else toast.success(`${bulkItems.length} controlli registrati come conformi`);
    if (!failed.length) setBulkDone(bulkItems.length);
    setBulkSaving(false);
    onRegistered();
  };
  // Ogni giorno selezionato viene registrato con la propria data, come se lo si
  // attestasse da solo; i controlli che chiedono un valore restano da compilare.
  const saveSelectedDays = async () => {
    setMultiSaving(true);
    let saved = 0, failed = 0, manual = 0, firstError = null;
    const daysToSave = [...multiDates].sort();
    for (const date of daysToSave) {
      try {
        const { data: dayData } = await api.get(`/app/procedures/today?site_id=${siteId}&date_key=${date}`);
        const procedures = dayData.procedures || [];
        const items = bulkItemsFor(procedures, equipment);
        manual += procedures.filter(isPendingProcedure).length - items.length;
        const results = await postBulk(items, date);
        results.forEach((result) => {
          if (result.status === "fulfilled") saved += 1;
          else { failed += 1; firstError = firstError || result.reason; }
        });
      } catch (error) { failed += 1; firstError = firstError || error; }
    }
    if (failed) toast.error(`${saved} controlli registrati, ${failed} non salvati: ${formatApiErrorDetail(firstError?.response?.data?.detail)}`);
    else toast.success(`Conformità attestata per ${daysToSave.length} ${daysToSave.length === 1 ? "giorno" : "giorni"}: ${saved} controlli registrati`);
    if (manual > 0) toast.warning(`${manual === 1 ? "Resta 1 controllo" : `Restano ${manual} controlli`} da compilare a mano (valori, temperature o ricevimento merci): quei giorni restano rossi finché non li completi.`);
    setMultiSaving(false);
    exitMultiMode();
    onRegistered();
  };
  const departmentNames = useMemo(
    () => Object.fromEntries((ctx.departments || []).map((department) => [department.id, department.name])),
    [ctx.departments],
  );
  const groupedProcedures = useMemo(() => {
    const groups = new Map();
    pendingProcedures.forEach((procedure) => {
      const departmentName = procedure.department_id
        ? (departmentNames[procedure.department_id] || "Reparto non specificato")
        : "Controlli generali della sede";
      if (!groups.has(departmentName)) groups.set(departmentName, []);
      groups.get(departmentName).push(procedure);
    });
    return Array.from(groups.entries());
  }, [departmentNames, pendingProcedures]);

  return (
    <div className="space-y-6" data-testid="controlli-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-body">Controlli HACCP</p>
          <div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Controlli di oggi</h1><AcademyTitleBadge /></div>
        </div>
        <div className="w-full sm:w-64">
          <Field label="Sede">
            <Select value={siteId} onChange={(e) => changeSite(e.target.value)} data-testid="controlli-site-select">
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start"><div className="space-y-3">{multiMode ? <Card className="border-2 border-violet-300 bg-violet-50 p-5" data-testid="multi-days-card">
        <h2 className="font-heading text-2xl font-bold text-violet-900">Seleziona i giorni rossi</h2>
        <p className="mt-1 text-sm font-medium text-violet-800">Tocca nel calendario i giorni rossi da attestare; toccali di nuovo per toglierli.</p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="multi-days-selected">{multiDates.length ? multiDates.map((date) => <span key={date} className="rounded-full bg-violet-600 px-3 py-1 text-sm font-semibold capitalize text-white">{shortDay(date)}</span>) : <span className="text-sm text-violet-700">Nessun giorno selezionato.</span>}</div>
        <Button variant="success" className={`mt-4 min-h-[60px] w-full text-lg font-bold uppercase tracking-wide ${multiSaving || !multiDates.length ? "" : "bulk-conforming-pending"}`} loading={multiSaving} disabled={!multiDates.length} onClick={saveSelectedDays} data-testid="multi-days-save"><ShieldCheck className="h-6 w-6" />Attesta la conformità di {multiDates.length} {multiDates.length === 1 ? "giorno" : "giorni"}</Button>
        <p className="mt-3 text-sm leading-relaxed text-violet-900">Premendo dichiari di aver verificato i controlli dei giorni scelti e che sono tutti conformi. I controlli che richiedono un valore restano da compilare giorno per giorno.</p>
        <Button variant="outline" className="mt-3 w-full" disabled={multiSaving} onClick={exitMultiMode} data-testid="multi-days-cancel">Annulla la selezione multipla</Button>
      </Card> : selectedDate ? <Card className="p-5" data-testid="controlli-date-card"><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Controlli da eseguire per</p><div className="mt-2 font-heading text-2xl font-semibold capitalize text-navy">{new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }).format(new Date(`${selectedDate}T12:00:00`))}</div>{selectedDate < todayKey() && <p className="mt-2 text-sm font-medium text-blue-700">Recupero controllo: la registrazione verrà attribuita alla giornata selezionata.</p>}</Card> : <Card className="border-2 border-red-300 bg-red-50 p-5" data-testid="controlli-choose-day"><div className="flex items-start gap-3"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-red-600 text-white"><CalendarDays className="h-6 w-6" /></span><div><h2 className="font-heading text-2xl font-bold text-red-800">Scegli il giorno da completare</h2><p className="mt-1 text-sm font-medium text-red-700">Tocca nel calendario il giorno che lampeggia in rosso: è quello da recuperare per primo.</p></div></div></Card>}
        {!readOnly && !multiMode && hasMissingDays && <Button variant="outline" className="min-h-[52px] w-full font-semibold uppercase tracking-wide !border-violet-400 !text-violet-800 hover:!bg-violet-50" onClick={() => { setMultiMode(true); setMultiDates([]); }} data-testid="multi-days-start"><CalendarDays className="h-5 w-5" />Seleziona più giorni rossi insieme</Button>}
      </div><ControlsCalendar siteId={siteId} selectedDate={selectedDate} onSelect={chooseDay} refreshKey={calendarRefresh} priorityPrompt onDaysLoaded={onDaysLoaded} multiSelect={multiMode} selectedDates={multiDates} /></div>

      {!selectedDate ? null : !data ? <Spinner /> : data.procedures.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">
          <ClipboardCheck className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <h2 className="font-heading text-xl font-semibold text-navy">{(data.closed_site_ids || []).includes(siteId) ? "Sede chiusa in questa giornata" : "Nessun controllo previsto oggi"}</h2>
          <p className="mt-2 text-sm">{(data.closed_site_ids || []).includes(siteId) ? "Nei giorni di chiusura i controlli giornalieri non sono previsti." : "Non risultano procedure programmate per questa sede."}</p>
        </Card>
      ) : pendingProcedures.length === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/50 p-10 text-center text-emerald-700" data-testid="all-controls-completed">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10" />
          <h2 className="font-heading text-xl font-semibold">{selectedDate === todayKey() ? "Tutti i controlli di oggi sono stati eseguiti" : "Tutti i controlli di questa giornata sono stati eseguiti"}</h2>
          <p className="mt-2 text-sm text-emerald-700/80">Non risultano attività ancora da completare per questa sede.</p>
        </Card>
      ) : (
        <div ref={listRef} className="scroll-mt-[calc(5rem+env(safe-area-inset-top))] space-y-6" data-testid="pending-controls-list">
          {bulkItems.length > 0 ? (
            <Card ref={bulkRef} className="scroll-mt-[calc(5rem+env(safe-area-inset-top))] border-2 border-red-300 bg-red-50 p-4 sm:p-5" data-testid="bulk-conforming-card">
              <p className="mb-2 flex items-center justify-center gap-2 text-center font-heading text-lg font-bold uppercase tracking-wide text-red-700" data-testid="bulk-conforming-hint"><ArrowDown className="h-5 w-5 animate-bounce" />Clicca qui<ArrowDown className="h-5 w-5 animate-bounce" /></p>
              <Button variant="success" className={`min-h-[60px] w-full text-lg font-bold uppercase tracking-wide ${bulkSaving ? "" : "bulk-conforming-pending"}`} loading={bulkSaving} onClick={saveAllConforming} data-testid="bulk-conforming-save"><ShieldCheck className="h-6 w-6" />Attesta la conformità di {bulkItems.length} {bulkItems.length === 1 ? "controllo" : "controlli"}</Button>
              <p className="mt-3 text-sm leading-relaxed text-red-900">Premendo dichiari di aver verificato questi controlli e che sono tutti conformi. Se qualcosa non va, apri prima la registrazione dettagliata qui sotto e segna “Non conforme” sulla sua riga, poi premi qui.</p>
              {manualCount > 0 && <p className="mt-2 text-sm font-semibold text-amber-800">{manualCount === 1 ? "Resta 1 controllo" : `Restano ${manualCount} controlli`} da compilare nella registrazione dettagliata (valori, temperature o ricevimento merci).</p>}
            </Card>
          ) : bulkDone > 0 && (
            <Card className="border-2 border-emerald-300 bg-emerald-50 p-4 sm:p-5" data-testid="bulk-conforming-done">
              <div className="flex min-h-[60px] w-full items-center justify-center gap-2 rounded-lg bg-haccp-success px-5 py-2.5 text-center text-lg font-medium text-white"><CheckCircle2 className="h-6 w-6" />Conformità attestata: {bulkDone} {bulkDone === 1 ? "controllo registrato" : "controlli registrati"}</div>
              <p className="mt-3 text-sm font-semibold text-amber-800">{pendingProcedures.length === 1 ? "Resta 1 controllo" : `Restano ${pendingProcedures.length} controlli`} da compilare qui sotto (valori, temperature o ricevimento merci).</p>
            </Card>
          )}
          {bulkItems.length > 0 && (
            <Button variant="outline" className="min-h-[52px] w-full font-semibold uppercase tracking-wide" onClick={() => setShowDetail((current) => !current)} aria-expanded={detailOpen} aria-controls="detailed-controls" data-testid="toggle-detailed-controls">
              {detailOpen ? <><ChevronUp className="h-5 w-5" />Chiudi la registrazione dettagliata</> : <><ChevronDown className="h-5 w-5" />Per la registrazione dettagliata clicca qui</>}
            </Button>
          )}
          {detailOpen && <div id="detailed-controls" className="space-y-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-semibold text-navy">Attività da completare</h2>
              <p className="mt-1 text-sm text-slate-500">Indica l’esito direttamente nella riga del controllo.</p>
            </div>
            <span className="shrink-0 rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-700">
              {pendingProcedures.length} {pendingProcedures.length === 1 ? "controllo" : "controlli"}
            </span>
          </div>

          {groupedProcedures.map(([departmentName, procedures]) => (
            <section key={departmentName} className="space-y-3" aria-labelledby={`department-${departmentName.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-3 border-b border-slate-200 pb-2">
                <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-navy" aria-hidden="true" />
                <h3 id={`department-${departmentName.replace(/\s+/g, "-").toLowerCase()}`} className="font-heading text-base font-bold text-navy">
                  Reparto: {departmentName}
                </h3>
                <span className="text-xs font-medium text-slate-400">({procedures.length})</span>
              </div>

              <div className="space-y-3">
                {procedures.map((p) => {
                  const description = p.description && p.description.trim().toLowerCase() !== p.name.trim().toLowerCase()
                    ? p.description
                    : null;
                  const instructions = p.instructions && p.instructions.trim() !== p.description?.trim()
                    ? p.instructions
                    : null;
                  return (
                    <Card key={p.id} className="border-l-4 border-l-red-500 p-4 sm:p-5" data-testid={`proc-${p.id}`}>
                      <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600">
                              {departmentName}
                            </span>
                            {p.is_ccp && <span className="rounded bg-navy px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-bright">CCP</span>}
                            <StatusBadge status="giallo" testid={`proc-status-${p.id}`}>Da eseguire</StatusBadge>
                          </div>
                          <h4 className="font-heading text-lg font-semibold text-navy">{p.name}</h4>
                          {periodLabel(p) && <p className="mt-1 text-sm font-semibold text-red-700">{periodLabel(p)}</p>}
                          {description && <p className="mt-1 text-sm leading-relaxed text-slate-500">{description}</p>}
                          {instructions && <p className="mt-2 text-sm font-medium leading-relaxed text-slate-600">Indicazioni: {instructions}</p>}
                        </div>
                        <div className="w-full lg:w-[22rem] lg:shrink-0">
                          <InlineControl key={`${p.id}-${selectedDate}`} procedure={p} selectedDate={selectedDate} onDone={onRegistered} readOnly={readOnly} temperatureItems={isTemperatureControl(p) ? temperatureItemsFor(p, equipment, data.procedures) : []} />
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>
          ))}
          </div>}
        </div>
      )}

      {selectedDate && data && detailOpen && periodicProcedures.length > 0 && (
        <section className="space-y-3" data-testid="periodic-controls-list">
          <div>
            <h2 className="font-heading text-xl font-semibold text-navy">Non ancora dovuti: in programma e su richiesta</h2>
            <p className="mt-1 text-sm text-slate-500">Questi controlli non rendono rossa la giornata. Settimanali e mensili diventano da fare dal loro giorno fisso (puoi registrarli anche prima); quelli su richiesta si registrano solo quando serve.</p>
          </div>
          {periodicProcedures.map((p) => (
            <Card key={p.id} className="border-l-4 border-l-blue-400 p-4" data-testid={`periodic-${p.id}`}>
              <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <h4 className="font-heading text-base font-semibold text-navy">{p.name}</h4>
                  <p className="mt-1 text-sm font-medium text-blue-700">{p.receipt_linked ? "Si registra al ricevimento merci, quando si scaricano le merci" : periodLabel(p)}</p>
                </div>
                <div className="w-full lg:w-[22rem] lg:shrink-0">
                  <InlineControl key={`${p.id}-${selectedDate}`} procedure={p} selectedDate={selectedDate} onDone={onRegistered} readOnly={readOnly} temperatureItems={isTemperatureControl(p) ? temperatureItemsFor(p, equipment, data.procedures) : []} />
                </div>
              </div>
            </Card>
          ))}
        </section>
      )}

    </div>
  );
}

